import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchMilestones } from "@/lib/api";

const kebabToPascal = (s) => (s || "star").split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");

export default function Timeline() {
  const { t, lang, dir } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchMilestones(lang)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [lang]);

  if (!items.length) return null;

  return (
    <section id="timeline" className="relative section-pad overflow-hidden" data-testid="timeline-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 40%, rgba(0,242,96,0.08), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
        <div className="text-center mb-14">
          <Reveal><SectionTag>{t.timeline.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{t.timeline.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{t.timeline.subtitle}</p>
          </Reveal>
        </div>

        <div className="relative">
          {/* Vertical line */}
          <div className={`absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent ${dir === "rtl" ? "right-6 md:right-1/2" : "left-6 md:left-1/2"}`} />
          <div className="space-y-8">
            {items.map((m, i) => {
              const IconCmp = LucideIcons[kebabToPascal(m.icon)] || LucideIcons.Star;
              const alignRight = i % 2 === 1;
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, x: dir === "rtl" ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className={`relative grid md:grid-cols-2 gap-4 md:gap-8 ${alignRight ? "md:[direction:rtl]" : ""}`}
                  data-testid={`timeline-item-${i}`}
                >
                  {/* Icon node */}
                  <div className={`absolute top-4 h-12 w-12 rounded-full grid place-items-center border-2 border-[color:var(--primary)] bg-[#050914] z-10 ${dir === "rtl" ? "right-0 md:right-1/2 md:translate-x-1/2" : "left-0 md:left-1/2 md:-translate-x-1/2"}`} style={{ boxShadow: "0 0 25px rgba(0,242,96,0.3)" }}>
                    <IconCmp size={18} className="text-primary" />
                  </div>

                  <div className={`card-surface rounded-2xl p-6 ${dir === "rtl" ? "mr-16 md:mr-0" : "ml-16 md:ml-0"} ${alignRight ? "md:mr-10 [direction:ltr]" : "md:ml-10"}`} dir="auto">
                    <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold mb-3" style={{ background: "rgba(0,242,96,0.15)", color: "var(--primary)" }}>
                      {m.year}
                    </div>
                    <h3 className="text-lg font-extrabold mb-2">{m.title}</h3>
                    {m.description && <p className="text-slate-400 text-sm leading-relaxed">{m.description}</p>}
                  </div>
                  <div className="hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
